import React from 'react';
import { motion } from 'framer-motion';
import { FileText, ListChecks, Upload, PlayCircle, FileCheck2, ChevronRight } from 'lucide-react';
import { Reveal } from './Reveal';

const steps = [
  { icon: FileText, title: 'Create shipment', desc: 'Register route, product, mode and incoterms in seconds.' },
  { icon: ListChecks, title: 'Generate checklist', desc: 'System proposes the required documents for the lane.' },
  { icon: Upload, title: 'Upload documents', desc: 'Attach commercial, transport and regulatory documents.' },
  { icon: PlayCircle, title: 'Run validation', desc: 'Cross-document checks flag blocking and advisory issues.' },
  { icon: FileCheck2, title: 'Generate report', desc: 'Auditable, pre-submission compliance summary.' },
];

const EASE = [0.16, 1, 0.3, 1];

export default function WorkflowSection() {
  return (
    <section id="workflow" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1674503384574-bd98cf380a78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBkYXJrJTIwcG9ydHxlbnwwfHx8fDE3NzgwODIxNjJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-[#071120]/88" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <Reveal className="max-w-3xl mb-14 md:mb-20">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-glow font-mono mb-4">Operational workflow</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
            Five steps from shipment registration to audit-ready report
          </h2>
          <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl">
            A linear, repeatable pre-shipment review designed for compliance teams under deadline.
          </p>
        </Reveal>

        {/* Connector line behind cards on desktop */}
        <div className="relative">
          <div className="hidden lg:block absolute left-0 right-0 top-[68px] h-px bg-gradient-to-r from-transparent via-cyan-glow/20 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.65, delay: i * 0.08, ease: EASE }}
                className="relative"
                data-testid={`workflow-step-${i + 1}`}
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.25 }}
                  className="relative h-full rounded-xl border border-white/10 bg-navy-900/70 backdrop-blur-sm p-6 hover:border-cyan-glow/40 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <div className="h-9 w-9 rounded-lg bg-cyan-glow/10 border border-cyan-glow/20 grid place-items-center text-cyan-glow">
                      <s.icon className="h-4.5 w-4.5" />
                    </div>
                    <span className="text-[10px] font-mono text-slate-500 tracking-widest">STEP · {String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="font-display font-semibold text-white text-base">{s.title}</div>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{s.desc}</p>
                </motion.div>

                {/* Arrow between steps (desktop) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-2 -translate-y-1/2 h-8 w-8 items-center justify-center rounded-full bg-navy-950 border border-cyan-glow/20 z-10">
                    <ChevronRight className="h-3.5 w-3.5 text-cyan-glow" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
